import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useIdleTimer } from "@/hooks/use-idle-timer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, AlertCircle } from "lucide-react";

const IDLE_MS = 2 * 60 * 60 * 1000;
const WARNING_LEAD_MS = 5 * 60 * 1000;
const WARNING_MS = IDLE_MS - WARNING_LEAD_MS;
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const KEEP_LOGGED_IN_KEY = "quotrak:keep-logged-in";

const POPUP_MESSAGE_TYPE = "quotrak:reauth";

function getGoogleReauthUrl(returnTo: string, popup: boolean): string {
  const url = new URL(`${BASE}/api/auth/google`, window.location.origin);
  url.searchParams.set("returnTo", returnTo);
  if (popup) url.searchParams.set("reauth_popup", "1");
  return url.toString();
}

interface IdleLockGateProps {
  children: ReactNode;
}

export function IdleLockGate({ children }: IdleLockGateProps) {
  const { user, login, logout } = useAuth();
  const [forcedWarning, setForcedWarning] = useState(false);
  const keepLoggedIn = localStorage.getItem(KEEP_LOGGED_IN_KEY) === "1";
  const enabled = !!user && !keepLoggedIn;
  const { state, reset, lock } = useIdleTimer({
    enabled,
    idleMs: IDLE_MS,
    warningMs: WARNING_MS,
  });
  const effectiveState: typeof state = forcedWarning && state === "active" ? "warning" : state;

  // Dev-only: expose helpers so tests can simulate the lock without waiting 2h.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __quotrakIdle?: unknown }).__quotrakIdle = {
      state: effectiveState,
      lock,
      reset: () => { setForcedWarning(false); reset(); },
      warn: () => setForcedWarning(true),
    };
  }, [effectiveState, lock, reset]);

  const handleStayActive = useCallback(() => {
    setForcedWarning(false);
    reset();
  }, [reset]);

  return (
    <>
      {children}
      {enabled && effectiveState === "warning" && (
        <IdleWarningBanner onStay={handleStayActive} />
      )}
      {enabled && effectiveState === "locked" && user && (
        <ReauthModal
          email={user.email}
          hasPassword={user.hasPassword}
          onUnlock={handleStayActive}
          onLogout={logout}
          loginFn={login}
          lockFn={lock}
        />
      )}
    </>
  );
}

function IdleWarningBanner({ onStay }: { onStay: () => void }) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="idle-warning-banner"
      style={{
        position: "fixed",
        top: "1rem",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 60,
        background: "#fef3c7",
        border: "1px solid #f59e0b",
        borderRadius: "0.5rem",
        padding: "0.625rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        maxWidth: "calc(100vw - 2rem)",
      }}
    >
      <AlertCircle style={{ width: "1.125rem", height: "1.125rem", color: "#b45309", flexShrink: 0 }} />
      <span style={{ fontSize: "0.875rem", color: "#78350f" }}>
        You'll be locked out in 5 minutes due to inactivity.
      </span>
      <button
        type="button"
        onClick={onStay}
        data-testid="idle-warning-stay"
        style={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: "#78350f",
          background: "#fde68a",
          border: "1px solid #f59e0b",
          borderRadius: "0.375rem",
          padding: "0.25rem 0.75rem",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Stay signed in
      </button>
    </div>
  );
}

interface ReauthModalProps {
  email: string;
  hasPassword: boolean;
  onUnlock: () => void;
  onLogout: () => Promise<void>;
  loginFn: (email: string, password: string) => Promise<void>;
  lockFn: () => void;
}

function ReauthModal({ email, hasPassword, onUnlock, onLogout, loginFn, lockFn }: ReauthModalProps) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [signingOut, setSigningOut] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Focus password input on open (when shown).
  useEffect(() => {
    if (hasPassword) {
      passwordInputRef.current?.focus();
    }
  }, [hasPassword]);

  // Listen for re-auth success from the OAuth popup.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; ok?: boolean } | null;
      if (!data || data.type !== POPUP_MESSAGE_TYPE) return;
      if (data.ok) {
        onUnlock();
      } else {
        setError("Google sign-in failed. Please try again.");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onUnlock]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !password) return;
    setSubmitting(true);
    setError("");
    try {
      await loginFn(email, password);
      setPassword("");
      onUnlock();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sign-in failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [email, password, submitting, loginFn, onUnlock]);

  const handleGoogle = useCallback(() => {
    setError("");
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    const popupUrl = getGoogleReauthUrl(returnTo, true);
    const popup = window.open(popupUrl, "quotrak-reauth", "width=500,height=620,menubar=no,toolbar=no");
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      // Popup blocked — fall back to same-tab redirect.
      window.location.href = getGoogleReauthUrl(returnTo, false);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await onLogout();
    } catch {
      setSigningOut(false);
      setError("Sign-out failed. Please try again.");
    }
  }, [onLogout, signingOut]);

  // Prevent ESC/backdrop click from dismissing.
  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  // Stop ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") e.preventDefault();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  // Suppress unused warning — lockFn intentionally exposed for future hooks.
  void lockFn;

  return (
    <div
      data-testid="reauth-modal-overlay"
      onClick={onBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(2px)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reauth-modal-title"
        data-testid="reauth-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "0.75rem",
          width: "100%",
          maxWidth: "26rem",
          padding: "1.5rem",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "1rem" }}>
          <div style={{
            width: "2.75rem",
            height: "2.75rem",
            borderRadius: "9999px",
            background: "#eff6ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "0.625rem",
          }}>
            <Lock style={{ width: "1.25rem", height: "1.25rem", color: "#2563eb" }} />
          </div>
          <h2 id="reauth-modal-title" style={{ fontSize: "1.125rem", fontWeight: 600, color: "#0f172a", margin: 0 }}>
            Session locked
          </h2>
          <p style={{ fontSize: "0.875rem", color: "#64748b", marginTop: "0.25rem", textAlign: "center" }}>
            For your security, this tab was locked after 2 hours of inactivity. Sign in again to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
            <Label htmlFor="reauth-email">Email</Label>
            <Input id="reauth-email" type="email" value={email} readOnly disabled data-testid="reauth-email" />
          </div>

          {hasPassword && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
              <Label htmlFor="reauth-password">Password</Label>
              <Input
                id="reauth-password"
                ref={passwordInputRef}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="reauth-password"
              />
            </div>
          )}

          {error && (
            <p role="alert" data-testid="reauth-error" style={{ fontSize: "0.8125rem", color: "#dc2626", margin: 0 }}>
              {error}
            </p>
          )}

          {hasPassword && (
            <Button
              type="submit"
              disabled={submitting || !password}
              data-testid="reauth-submit"
              style={{ width: "100%" }}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Continue
            </Button>
          )}
        </form>

        {!hasPassword && (
          // Google-only account — show only the Google button (no divider).
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogle}
            data-testid="reauth-google"
            style={{ width: "100%", marginTop: "0.75rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}
          >
            <GoogleIcon />
            Continue with Google
          </Button>
        )}

        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            data-testid="reauth-signout"
            style={{
              fontSize: "0.8125rem",
              color: "#64748b",
              background: "none",
              border: "none",
              cursor: signingOut ? "not-allowed" : "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            {signingOut ? "Signing out…" : "Sign out instead"}
          </button>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
